/*
 * Behaviour for sections/custom-clients-marquee.liquid: mouse dragging for the
 * logo strip, so a visitor can pull it along to look at a logo that has already
 * gone past.
 *
 * Loaded by that section only, and layered on top of `marquee.js`, which is left
 * exactly as it is — it measures the row, repeats it, and runs the CSS animation
 * in custom-clients-marquee.css.
 *
 * The strip's position *is* that animation, and one iteration of it is exactly
 * one loop of the row — so a drag moves nothing itself, it seeks the animation.
 * The loop therefore stays seamless, the only measurement needed is the
 * pixels-to-milliseconds rate below, and letting go leaves the strip running
 * from where it was dropped.
 *
 * `marquee.js` already eases the strip to a stop under the cursor, so there is
 * nothing here to stop it; the pause is only for the tail of that easing, which
 * is debounced and can still be running when a visitor grabs the strip straight
 * away.
 *
 * With no animation to seek — a visitor who asked for reduced motion, or a
 * `marquee.js` that never measured the row — a drag does nothing, which leaves
 * the static strip those cases already fall back to.
 */
class ClientsDragMarquee extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    // Dragging an image is a browser gesture of its own, and it would win.
    this.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /** @type {Animation | null} */
  #animation = null;

  /** @type {number | null} */
  #pointerId = null;

  #pointerX = 0;

  /** Milliseconds of animation per pixel dragged. @type {number} */
  #timePerPixel = 0;

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Touch and pen are left to the page; secondary buttons to the browser.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.#pointerId != null) return;

    const marquee = this.querySelector('marquee-component');
    const track = this.querySelector('.clients-marquee__track');
    /*
     * Read on every drag rather than cached: `marquee.js` restarts the
     * animation on resize, and there is none at all until it has measured the
     * row and set `--marquee-speed`.
     */
    const animation = track
      ?.getAnimations()
      .find((candidate) => candidate.animationName === 'clients-marquee-scroll');
    const timePerPixel = animation && track ? getTimePerPixel(animation, track) : 0;

    if (!marquee || !animation || !timePerPixel) return;

    this.#animation = animation;
    this.#timePerPixel = timePerPixel;
    this.#pointerId = event.pointerId;
    this.#pointerX = event.clientX;
    animation.pause();
    this.setAttribute('dragging', '');
    /*
     * Captured on the marquee, not on this element, so the pointer never looks
     * to `marquee.js` like it left the strip: it owns the hover slowdown, and a
     * leave halfway through a drag would set the strip moving again.
     */
    marquee.setPointerCapture(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (event.pointerId !== this.#pointerId || !this.#animation) return;

    // A button released outside the window can arrive as a move rather than an
    // up, which would otherwise leave the strip stuck to the cursor.
    if (event.buttons === 0) return this.#handlePointerUp(event);

    const distance = event.clientX - this.#pointerX;
    this.#pointerX = event.clientX;

    // Dragging right runs the animation backwards.
    let time = Number(this.#animation.currentTime ?? 0) - distance * this.#timePerPixel;

    /*
     * A negative time is the animation's before-phase, which would snap the
     * strip back to its starting position. It repeats forever, so whole
     * iterations are interchangeable: shift up into one rather than clamp at 0.
     */
    if (time < 0) {
      const duration = Number(this.#animation.effect?.getComputedTiming().duration ?? 0);

      time += Math.ceil(-time / duration) * duration;
    }

    this.#animation.currentTime = time;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    this.#pointerId = null;
    this.removeAttribute('dragging');
    /*
     * Resumes at whatever rate `marquee.js` currently wants: still stopped while
     * the cursor rests on the strip, back up to full speed once it leaves.
     */
    this.#animation?.play();
    this.#animation = null;
  };
}

/**
 * How much animation time one dragged pixel is worth.
 *
 * The keyframe travels half the track's width plus half a gap — one full copy
 * of the row, the distance that makes the loop seamless — per iteration.
 *
 * @param {Animation} animation
 * @param {Element} track
 * @returns {number} Milliseconds per pixel, or 0 if it cannot be measured yet.
 */
function getTimePerPixel(animation, track) {
  const duration = Number(animation.effect?.getComputedTiming().duration ?? 0);
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const distance = track.getBoundingClientRect().width / 2 + gap / 2;

  return duration > 0 && distance > 0 ? duration / distance : 0;
}

if (!customElements.get('custom-drag-marquee')) {
  customElements.define('custom-drag-marquee', ClientsDragMarquee);
}
